/**
 * Aba "Perfil de Operação" da Tela A2 (#313 · ADR-030 Eixo N).
 *
 * Mostra o perfil ativo + os defaults reais que ele aplica (privacyProfile +
 * defaultRoutingStrategy — os únicos campos de Tenant que o apply seta hoje,
 * por honestidade R15) + os 4 presets selecionáveis. Aplicar é destrutivo
 * (troca config em massa + evento auditado `high`) → confirmação vinculante
 * com digitação do nome do perfil antes do POST /apply.
 *
 * Admin Hub T9 fix round 1 — `<header>` próprio (h2 "Perfil de operação" +
 * parágrafo) removido: dentro de uma `DomainSection` o título/descrição já
 * vêm do catálogo (item.label/item.efeito), mesmo racional que `GeneralTab`
 * (T7) e `ConductTab`/`SilentTab`/`BehavioralTab` (T8) já registraram. A
 * frase do parágrafo removido ("Um perfil aplica defaults coerentes de
 * privacidade e distribuição de fila. Trocar de perfil é um evento
 * auditado.") era mais fiel à prancha 06 do que o `efeito` anterior do
 * catálogo — dobrada em `hub/catalog.ts` (item `perfil-de-operacao`) em vez
 * de virar um segundo heading, mesma disciplina que a T8 já aplicou.
 * NENHUMA parte da máquina de confirmação vinculante abaixo (`confirming`,
 * `handleConfirmApply`, o botão "Aplicar perfil selecionado", o `Modal` de
 * confirmação) foi tocada — ela não entra na `StickySaveBar` e mantém botão
 * + confirmação próprios (CLAUDE.md §10, ação de risco).
 */

import { Modal } from '@lume/ui-components';
import { useState } from 'react';

import {
  OPERATION_PROFILE_META,
  PRIVACY_PROFILE_LABELS,
  ROUTING_STRATEGY_LABELS,
} from './config-tenant-labels';
import { useApplyOperationProfile, useOperationProfile } from './use-tenant-config';

import type { OperationProfileId, OperationProfilePresetWire } from './types';

export function OperationProfileTab({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const query = useOperationProfile();
  const apply = useApplyOperationProfile();

  const [selected, setSelected] = useState<OperationProfileId | null>(null);
  const [confirming, setConfirming] = useState<OperationProfileId | null>(null);

  if (query.isLoading) {
    return <p className="text-sm text-text-secondary">Carregando perfil de operação…</p>;
  }
  if (query.isError || query.data === undefined) {
    return <p className="text-sm text-red-600">Falha ao carregar o perfil de operação.</p>;
  }

  const state = query.data;
  const presetToConfirm =
    confirming !== null ? (state.presets.find((p) => p.profile === confirming) ?? null) : null;

  const handleConfirmApply = (): void => {
    if (confirming === null) return;
    apply.mutate(confirming, {
      onSuccess: () => {
        setSelected(null);
        setConfirming(null);
      },
    });
  };

  return (
    <div className="max-w-3xl">
      {/* Header próprio saiu (Admin Hub T9 fix round 1) — DomainSection já
          supre título (item.label) + descrição (item.efeito, dobrada com a
          frase que vivia aqui) quando esta aba é composta no hub. */}
      {/* Perfil ativo */}
      <section className="mb-8 rounded-xl border border-accent/40 bg-surface p-5">
        <span className="text-xs font-medium uppercase tracking-wide text-text-secondary">
          Perfil ativo
        </span>
        {state.current === null ? (
          <p className="mt-2 text-sm text-text-primary">
            Nenhum perfil aplicado ainda. Escolha um abaixo para definir os defaults.
          </p>
        ) : (
          <div className="mt-2 flex items-center gap-3">
            <span className="text-2xl">{OPERATION_PROFILE_META[state.current].icon}</span>
            <div>
              <p className="text-base font-semibold text-text-primary">
                {OPERATION_PROFILE_META[state.current].name}
              </p>
              <p className="text-xs text-text-secondary">
                {OPERATION_PROFILE_META[state.current].size}
              </p>
            </div>
          </div>
        )}
        <dl className="mt-4 grid grid-cols-2 gap-4 border-t border-border-subtle pt-4 text-sm">
          <div>
            <dt className="text-xs text-text-secondary">Perfil de privacidade</dt>
            <dd className="mt-0.5 text-text-primary">
              {PRIVACY_PROFILE_LABELS[state.privacyProfile]}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-text-secondary">Distribuição de fila</dt>
            <dd className="mt-0.5 text-text-primary">
              {ROUTING_STRATEGY_LABELS[state.defaultRoutingStrategy]}
            </dd>
          </div>
        </dl>
      </section>

      {/* Presets */}
      <h3 className="mb-3 text-sm font-medium text-text-primary">Perfis disponíveis</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {state.presets.map((preset) => (
          <PresetCard
            key={preset.profile}
            preset={preset}
            isCurrent={state.current === preset.profile}
            isSelected={selected === preset.profile}
            canManage={canManage}
            onSelect={() => setSelected(preset.profile)}
          />
        ))}
      </div>

      {canManage ? (
        <div className="mt-6 flex items-center justify-end">
          <button
            type="button"
            disabled={selected === null || selected === state.current}
            onClick={() => selected !== null && setConfirming(selected)}
            className="rounded bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {selected !== null && selected === state.current
              ? 'Perfil já ativo'
              : 'Aplicar perfil selecionado'}
          </button>
        </div>
      ) : (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A aplicação de perfis de operação é restrita ao administrador
          do tenant.
        </p>
      )}

      {/* Confirmação vinculante */}
      {presetToConfirm !== null && (
        <Modal
          isOpen
          destructive
          title={`Aplicar perfil "${OPERATION_PROFILE_META[presetToConfirm.profile].name}"?`}
          requireConfirmText={OPERATION_PROFILE_META[presetToConfirm.profile].name}
          confirmLabel={apply.isPending ? 'Aplicando…' : 'Aplicar perfil'}
          cancelLabel="Cancelar"
          onClose={() => {
            if (!apply.isPending) setConfirming(null);
          }}
          onConfirm={handleConfirmApply}
        >
          <ApplyDiff preset={presetToConfirm} />
          {apply.isError && (
            <p role="alert" className="mt-3 text-sm text-red-600">
              Falha ao aplicar o perfil. Tente novamente.
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

function PresetCard({
  preset,
  isCurrent,
  isSelected,
  canManage,
  onSelect,
}: {
  readonly preset: OperationProfilePresetWire;
  readonly isCurrent: boolean;
  readonly isSelected: boolean;
  readonly canManage: boolean;
  readonly onSelect: () => void;
}): JSX.Element {
  const meta = OPERATION_PROFILE_META[preset.profile];
  return (
    <button
      type="button"
      disabled={!canManage}
      onClick={onSelect}
      aria-pressed={isSelected}
      className={`flex flex-col gap-2 rounded-xl border p-4 text-left transition-colors disabled:cursor-default ${
        isSelected ? 'border-accent bg-accent/5' : 'border-border-subtle bg-surface'
      } ${canManage ? 'hover:border-accent/60' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-xl">{meta.icon}</span>
        <span className="text-sm font-semibold text-text-primary">{meta.name}</span>
        {isCurrent && (
          <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-800">
            Ativo
          </span>
        )}
      </div>
      <p className="text-xs text-text-secondary">{meta.size}</p>
      <p className="text-xs text-text-secondary">{meta.who}</p>
      <dl className="mt-1 space-y-1 border-t border-border-subtle pt-2 text-xs">
        <div className="flex justify-between gap-2">
          <dt className="text-text-secondary">Privacidade</dt>
          <dd className="text-text-primary">{PRIVACY_PROFILE_LABELS[preset.privacyProfile]}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-text-secondary">Fila</dt>
          <dd className="text-text-primary">
            {ROUTING_STRATEGY_LABELS[preset.defaultRoutingStrategy]}
          </dd>
        </div>
      </dl>
    </button>
  );
}

function ApplyDiff({ preset }: { readonly preset: OperationProfilePresetWire }): JSX.Element {
  return (
    <div className="text-sm text-text-secondary">
      <p>
        Este perfil substituirá os defaults de operação do tenant. As alterações são auditadas.
        Confirme os novos valores:
      </p>
      <ul className="mt-3 space-y-1.5">
        <li className="flex justify-between gap-2">
          <span>Perfil de privacidade</span>
          <strong className="text-text-primary">
            {PRIVACY_PROFILE_LABELS[preset.privacyProfile]}
          </strong>
        </li>
        <li className="flex justify-between gap-2">
          <span>Distribuição de fila</span>
          <strong className="text-text-primary">
            {ROUTING_STRATEGY_LABELS[preset.defaultRoutingStrategy]}
          </strong>
        </li>
      </ul>
    </div>
  );
}
